import React, { useEffect, useState } from 'react';
import { Box, VStack, HStack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { HiOutlineTableCells, HiXMark, HiChevronDown } from 'react-icons/hi2';

const formatNumber = (n, lang) => (n == null ? '' : Number(n).toLocaleString(lang, { maximumFractionDigits: 2 }));

const RuleRow = ({ label, points, disqualify, colors, t, lang }) => (
    <HStack
        justify="space-between"
        gap={3}
        py={2}
        borderTopWidth="1px"
        style={{ borderColor: colors.borderColor }}
    >
        <Text fontSize="sm" style={{ color: colors.textPrimary }} wordBreak="break-word">{label}</Text>
        <Text
            fontSize="sm"
            fontWeight="600"
            flexShrink={0}
            style={{ color: disqualify ? '#E5484D' : colors.textPrimary }}
        >
            {disqualify ? t('scoring.disqualifies') : t('scoring.points', { value: formatNumber(points, lang) })}
        </Text>
    </HStack>
);

const rangeLabel = (r, t, lang) => {
    const min = formatNumber(r.min, lang);
    const max = formatNumber(r.max, lang);
    if (r.min != null && r.max != null) return r.min === r.max ? min : `${min} – ${max}`;
    if (r.min != null) return t('scoring.rangeFrom', { min });
    if (r.max != null) return t('scoring.rangeTo', { max });
    return t('scoring.rangeAny');
};

// Names may contain i18n separators, so look up the map directly.
const sectionName = (name, t) => {
    const known = t('scoring.sectionNames', { returnObjects: true });
    return (known && typeof known === 'object' && known[name]) || name;
};

const sectionRules = (section, t, lang) => {
    switch (section.scoringType) {
        case 'Value':
            return section.valueRules.map(r => ({ label: r.value, points: r.points, disqualify: r.disqualify }));
        case 'Range':
            return section.rangeRules.map(r => ({ label: rangeLabel(r, t, lang), points: r.points, disqualify: r.disqualify }));
        case 'Set':
            return section.setRules.map(r => ({ label: r.value, points: r.points }));
        case 'AiMatch':
            return section.aiLevels.map(l => ({ label: t(`scoring.aiLevels.${l.level}`, l.level), points: l.points }));
        default:
            return [];
    }
};

const Section = ({ section, colors, t, lang, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen);
    const rules = sectionRules(section, t, lang);
    const missingNote = section.missingPolicy !== 'Ignore'
        ? t(`scoring.missing.${section.missingPolicy}`, '')
        : '';

    return (
        <Box borderWidth="1px" borderRadius="12px" overflow="hidden" style={{ borderColor: colors.borderColor }}>
            <HStack
                as="button"
                w="full"
                justify="space-between"
                gap={3}
                px={4}
                py={3}
                cursor="pointer"
                textAlign="left"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                _hover={{ bg: colors.attachmentHover }}
                transition="background 0.15s"
            >
                <Text fontSize="sm" fontWeight="600" style={{ color: colors.textPrimary }}>
                    {sectionName(section.name, t)}
                </Text>
                <HStack gap={2} flexShrink={0}>
                    <Box
                        px={2}
                        py="2px"
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="600"
                        style={{ background: `${colors.avatarBg}1A`, color: colors.avatarBg }}
                    >
                        {t('scoring.maxPoints', { value: formatNumber(section.maxScore, lang) })}
                    </Box>
                    <Box
                        color={colors.iconColor}
                        transform={open ? 'rotate(180deg)' : 'none'}
                        transition="transform 0.2s"
                    >
                        <HiChevronDown size={16} />
                    </Box>
                </HStack>
            </HStack>

            {open && (
                <VStack align="stretch" gap={0} px={4} pb={3}>
                    {section.description && (
                        <Text fontSize="xs" lineHeight="1.5" pb={2} whiteSpace="pre-wrap" style={{ color: colors.textSecondary }}>
                            {section.description}
                        </Text>
                    )}
                    {rules.length > 0 ? (
                        rules.map((r, i) => <RuleRow key={i} {...r} colors={colors} t={t} lang={lang} />)
                    ) : (
                        <Text fontSize="xs" py={2} style={{ color: colors.textSecondary }}>
                            {t('scoring.noRules')}
                        </Text>
                    )}
                    {missingNote && (
                        <Text fontSize="xs" pt={2} style={{ color: colors.textSecondary }}>
                            {missingNote}
                        </Text>
                    )}
                </VStack>
            )}
        </Box>
    );
};

const ScoringMatrixDialog = ({ colors, matrix, onClose }) => {
    const { t, i18n } = useTranslation('chat');
    const lang = i18n.language;

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <Box
            position="fixed"
            inset="0"
            zIndex={1400}
            display="flex"
            alignItems={{ base: 'flex-end', md: 'center' }}
            justifyContent="center"
            p={{ base: 0, md: 6 }}
            bg="blackAlpha.500"
            backdropFilter="blur(4px)"
            role="dialog"
            aria-modal="true"
            aria-labelledby="scoring-title"
            onClick={onClose}
        >
            <Box
                w="full"
                maxW="560px"
                maxH={{ base: '92dvh', md: '85vh' }}
                overflowY="auto"
                borderTopRadius="20px"
                borderBottomRadius={{ base: 0, md: '20px' }}
                p={{ base: 5, md: 7 }}
                boxShadow="0 20px 60px rgba(0,0,0,0.25)"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: colors.chatBg,
                    color: colors.textPrimary,
                    paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
                }}
            >
                <VStack align="stretch" gap={5}>
                    <HStack align="flex-start" justify="space-between" gap={3}>
                        <VStack align="flex-start" gap={2}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                w="40px"
                                h="40px"
                                borderRadius="12px"
                                fontSize="22px"
                                style={{ background: `${colors.avatarBg}1A`, color: colors.avatarBg }}
                            >
                                <HiOutlineTableCells />
                            </Box>
                            <Text id="scoring-title" fontSize="lg" fontWeight="700">
                                {t('scoring.title')}
                            </Text>
                            <Text fontSize="sm" lineHeight="1.5" style={{ color: colors.textSecondary }}>
                                {t('scoring.intro', { value: formatNumber(matrix.totalMaxScore, lang) })}
                            </Text>
                        </VStack>
                        <Box
                            as="button"
                            p={2}
                            borderRadius="md"
                            color={colors.iconColor}
                            cursor="pointer"
                            onClick={onClose}
                            _hover={{ bg: colors.attachmentHover }}
                            aria-label={t('scoring.close')}
                        >
                            <HiXMark size={20} />
                        </Box>
                    </HStack>

                    <VStack align="stretch" gap={3}>
                        {matrix.sections.map((s, i) => (
                            <Section key={s.key} section={s} colors={colors} t={t} lang={lang} defaultOpen={i === 0} />
                        ))}
                    </VStack>
                </VStack>
            </Box>
        </Box>
    );
};

export default ScoringMatrixDialog;
